import { useAnalytics } from "@codat/analytics";
import {
  Button,
  Modal,
  ModalBody,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import { ApiError } from "api/shared";
import { RefetchFunction } from "axios-hooks";
import React, { FC } from "react";

import styles from "./DeleteApiKeyModal.module.css";

export interface DeleteApiKeyModalProps {
  onCancel: () => void;
  onSuccess: () => void;
  deleteApiKey: RefetchFunction<ApiError, boolean>;
  deletingLoading: boolean;
}

const DeleteApiKeyModal: FC<DeleteApiKeyModalProps> = ({
  onCancel,
  onSuccess,
  deleteApiKey,
  deletingLoading,
}: DeleteApiKeyModalProps) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const revokeApiKey = () => {
    deleteApiKey()
      .then(() => {
        addToast({
          message: "Revoked API key",
          variant: "success",
        });
        onSuccess();
      })
      .catch((error) => {
        addToast({
          message: `Unable to revoke API key: ${error}`,
          variant: "error",
        });
      });
  };

  return (
    <Modal onClose={onCancel}>
      <ModalBody
        size="medium"
        title="Revoke API key"
        data-testid="revoke-api-key-modal"
        onClose={onCancel}
      >
        <Typography as="h2" variant="body_md">
          Are you sure you want to revoke access to this API key?
        </Typography>{" "}
        <Typography as="p" variant="body_md">
          Doing so will invalidate all requests or services using this API key
          to authenticate with the Codat API.
        </Typography>
        <div className={styles.buttonContainer}>
          <Button
            data-testid="cancel-button"
            variant="secondary"
            color="interface"
            label="Cancel"
            onClick={() => {
              sendEvent("Cancel Revoke Button - Click");
              onCancel();
            }}
          />
          <Button
            data-testid="revoke-key-button"
            variant="secondary"
            color="negative"
            label="Revoke API key"
            onClick={() => {
              sendEvent("Revoke API key button - Click");
              revokeApiKey();
            }}
            isLoading={deletingLoading}
          />
        </div>
      </ModalBody>
    </Modal>
  );
};

export default DeleteApiKeyModal;
